import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import {
  Doc,
  DocLocation,
  DocSearchFilter,
  PermissionsModel,
} from '../../../../types';
import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import {
  startDriveSearchTask,
  updateStarredDocStatus,
} from '../../../api/users-api';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import DriveSearchGridToolbar from './drive-search-grid-toolbar';
import { DriveSearchForm } from './drive-search-page';
import dayjs from 'dayjs';
import { ModuleList } from '../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { getVisibilityInfo } from '../../../utils/drive-visibility-utils';
import DriveSearchRestoreDialog from './drive-search-restore-dialog';
import DriveSearchTrashDialog from './drive-search-trash-dialog';
import DriveSearchDeleteDialog from './drive-search-delete-dialog';
import DriveSearchRenameDialog from './drive-search-rename-dialog';
import {
  DriveRoles,
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../utils/roles/client-roles';
import { UseFormHandleSubmit } from 'react-hook-form';
import SharedErrorBoundary from '../../../shared/shared-error-boundary';
import DriveFileEditPermissionsDialog from '../drive-file-edit-permissions-dialog';
import DriveSearchCopyDialog from './drive-search-copy-dialog';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import UserAvatar from '../../../shared/user-avatar';
import { useUserSettings } from '../../../utils/use-user-settings';
import DriveSearchLocation, { generateDriveUrl } from './drive-search-location';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import DriveDocLabelsCell from '../../../shared/drive-doc-labels-cell';
import { useDriveLabelBadges } from '../../../shared/hooks/use-drive-label-badges';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import AddToDrive from '@mui/icons-material/AddToDrive';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import DeleteForever from '@mui/icons-material/DeleteForever';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import FileCopy from '@mui/icons-material/FileCopy';
import FindInPage from '@mui/icons-material/FindInPage';
import Pageview from '@mui/icons-material/Pageview';
import RestoreFromTrash from '@mui/icons-material/RestoreFromTrash';
import Share from '@mui/icons-material/Share';
import Star from '@mui/icons-material/Star';
import StarOutline from '@mui/icons-material/StarOutline';
interface DriveSearchPageProps {
  changeLogRoles: string[];
  driveRoles: string[];
  searchedDocs: Doc[];
  primaryDomain: string;
  clickedDoc: Doc | null;
  driveSearchFilter: Partial<DriveSearchForm>;
  isOnlyOwnedFilterActive: boolean;
  loggedInUser: string;
  setClickedDoc: Dispatch<SetStateAction<Doc | null>>;
  setSearchedDocs: Dispatch<SetStateAction<Doc[]>>;
  handleSubmit: UseFormHandleSubmit<
    Partial<DriveSearchForm>,
    Partial<DriveSearchForm>
  >;
  mutate: UseMutateFunction<
    string,
    Error,
    { data: Partial<DocSearchFilter>; changeLogMessage: string },
    unknown
  >;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  erroredUsers: string[];
  numberOfSearchedUsers: number;
  fromUserPage: boolean;
  firebaseMessageType: string;
}

export default function DriveSearchGrid({
  searchedDocs,
  isLoading,
  primaryDomain,
  clickedDoc,
  changeLogRoles,
  driveRoles,
  loggedInUser,
  driveSearchFilter,
  isOnlyOwnedFilterActive,
  setClickedDoc,
  setSearchedDocs,
  handleSubmit,
  mutate,
  setIsLoading,
  erroredUsers,
  numberOfSearchedUsers,
  fromUserPage,
  firebaseMessageType,
}: DriveSearchPageProps) {
  const { createEvent } = useAnalytics();
  const [openTrashDialog, setOpenTrashDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [openEditShareDialog, setOpenEditShareDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [starDocSelection, setStarDocSelection] = useState<Doc>();
  const [openStarChangeLogDialog, setOpenStarChangeLogDialog] = useState(false);
  const [openExportChangeLogDialog, setOpenExportChangeLogDialog] =
    useState(false);
  const [openSearchChangeLogDialog, setOpenSearchChangeLogDialog] =
    useState(false);
  const [storedSearchData, setStoredSearchData] =
    useState<Partial<DriveSearchForm> | null>(null);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [hierarchy, setHierarchy] = useState<DocLocation[] | null>(null);
  const [hierarchyCache, setHierarchyCache] = useState<
    Record<string, DocLocation[]>
  >({});
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [isHierarchyLoading, setIsHierarchyLoading] = useState(false);
  const [appliedOnlyOwnedByMe, setAppliedOnlyOwnedByMe] = useState(false);
  const { setSnackbar, firebaseUser } = useStore();
  const { viewType } = useUserSettings();

  // Callback to handle visibility updates from permission dialogs
  const handleVisibilityUpdated = useCallback(
    (
      docId: string,
      foundUnder: string,
      updatedPermission: PermissionsModel | null
    ) => {
      setSearchedDocs((prevDocs) =>
        prevDocs.map((doc) => {
          if (doc.id === docId && doc.foundUnder === foundUnder) {
            const currentPerms = doc.permissions || [];

            if (!updatedPermission) {
              const filtered = currentPerms.filter(
                (p) => p.type !== 'anyone' && p.type !== 'domain'
              );
              return { ...doc, permissions: filtered };
            }

            const isVisibilityPerm =
              updatedPermission.type === 'anyone' ||
              updatedPermission.type === 'domain';

            const filtered = currentPerms.filter(
              (p) =>
                p.permId !== updatedPermission.permId &&
                (isVisibilityPerm
                  ? p.type !== 'anyone' && p.type !== 'domain'
                  : true)
            );
            return { ...doc, permissions: [...filtered, updatedPermission] };
          }
          return doc;
        })
      );
    },
    [setSearchedDocs]
  );

  const { mutate: mutateStarredStatus, isPending: isUpdatingStarredStatus } =
    useMutation({
      mutationFn: (data: Doc) =>
        updateStarredDocStatus(
          data.foundUnder,
          data.id,
          !data.starred,
          changeLogMessage
        ),
      onSuccess: (_data, selectedDoc) => {
        const isFolder = selectedDoc.mimeType.includes('folder');

        setSnackbar({
          open: true,
          severity: 'success',
          message: isFolder
            ? 'Folder updated successfully!'
            : 'Document updated successfully!',
        });

        const updatedDocs = searchedDocs.map((doc) => {
          if (
            doc.id === selectedDoc.id &&
            doc.foundUnder === selectedDoc.foundUnder
          ) {
            return {
              ...doc,
              starred: !doc.starred,
            };
          }
          return doc;
        });

        setSearchedDocs(updatedDocs);
        setChangeLogMessage('');
      },
      onError: (error, selectedDoc) => {
        handleErrors({
          error,
          snackbarMessage: `There was an issue updating this ${
            selectedDoc.mimeType?.includes('folder') ? 'folder' : 'document'
          }. Please refresh and try again.`,
        });
      },
      onSettled: () => {
        createEvent(analytics.drive.drive_search.star_drive_content, {
          module: fromUserPage
            ? ModuleList.modules.userManagement.name
            : ModuleList.modules.drive.name,
          section: fromUserPage
            ? ModuleList.modules.userManagement.sections.driveSearch
            : ModuleList.modules.drive.sections.driveSearch,
        });
        setOpenStarChangeLogDialog(false);
      },
    });

  const { mutate: exportMutate, isPending: isExporting } = useMutation({
    mutationFn: () => {
      const newSearch: Partial<DriveSearchForm> = {
        ...driveSearchFilter,
        modifiedDateOp:
          driveSearchFilter.dateType === 'modifiedDate' && driveSearchFilter.day
            ? driveSearchFilter.time
            : null,
        lastViewedOp:
          driveSearchFilter.dateType === 'lastViewedByMeDate' &&
          driveSearchFilter.day
            ? driveSearchFilter.time
            : null,
        modifiedDate:
          driveSearchFilter.dateType === 'modifiedDate' && driveSearchFilter.day
            ? dayjs(driveSearchFilter.day).toDate()
            : null,
        lastViewedByMeDate:
          driveSearchFilter.dateType === 'lastViewedByMeDate' &&
          driveSearchFilter.day
            ? dayjs(driveSearchFilter.day).toDate()
            : null,
      };
      if (driveSearchFilter.docID === '') {
        delete newSearch.docID;
      }
      if (!driveSearchFilter.isSuspended) {
        delete newSearch.isSuspended;
      }
      if (
        driveSearchFilter.mimeType === '' ||
        driveSearchFilter.mimeType === 'all'
      ) {
        delete newSearch.mimeType;
      }
      if (driveSearchFilter.title === '') {
        delete newSearch.title;
      }
      delete newSearch.day;
      delete newSearch.time;
      delete newSearch.dateType;
      return startDriveSearchTask(
        newSearch,
        firebaseUser.firebaseDbPath,
        true,
        changeLogMessage,
        firebaseMessageType
      );
    },
    onSuccess: (data) => {
      window.open(
        `https://docs.google.com/a/${
          loggedInUser.split('@')[1]
        }/spreadsheet/ccc?key=${data}#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue exporting the drive search results. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_search.export_drive_search_results, {
        module: fromUserPage
          ? ModuleList.modules.userManagement.name
          : ModuleList.modules.drive.name,
        section: fromUserPage
          ? ModuleList.modules.userManagement.sections.driveSearch
          : ModuleList.modules.drive.sections.driveSearch,
      });
      setOpenExportChangeLogDialog(false);
    },
  });

  const handleExportMutate = useCallback(() => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.CanExportDriveSearch.value
      )
    ) {
      setOpenExportChangeLogDialog(true);
    } else {
      exportMutate();
    }
  }, [changeLogRoles, exportMutate]);

  const performSearch = useCallback(
    (data: Partial<DriveSearchForm>, msg: string) => {
      setAppliedOnlyOwnedByMe(!!data.onlyOwnedByMe);
      const newSearch: Partial<DriveSearchForm> = {
        ...data,
        modifiedDateOp:
          data.dateType === 'modifiedDate' && data.day ? data.time : null,
        lastViewedOp:
          data.dateType === 'lastViewedByMeDate' && data.day ? data.time : null,
        modifiedDate:
          data.dateType === 'modifiedDate' && data.day
            ? dayjs(data.day).toDate()
            : null,
        lastViewedByMeDate:
          data.dateType === 'lastViewedByMeDate' && data.day
            ? dayjs(data.day).toDate()
            : null,
      };
      if (data.docID === '') {
        delete newSearch.docID;
      }
      if (data.mimeType === 'all') {
        delete newSearch.mimeType;
      }
      if (data.title === '') {
        delete newSearch.title;
      }
      delete newSearch.day;
      delete newSearch.time;
      delete newSearch.dateType;
      mutate({ data: newSearch, changeLogMessage: msg });
      setOpenSearchChangeLogDialog(false);
      setChangeLogMessage('');
    },
    []
  );

  const handleSearch = useCallback(
    (data: Partial<DriveSearchForm>) => {
      const hasDriveSearchChangeLogPermission = isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.ViewDriveSearch.value
      );

      const hasDriveSearchTabChangeLogPermission = isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.ViewDriveSearchTab.value
      );

      if (
        (fromUserPage && hasDriveSearchTabChangeLogPermission) ||
        (!fromUserPage && hasDriveSearchChangeLogPermission)
      ) {
        setStoredSearchData(data);
        setOpenSearchChangeLogDialog(true);
      } else {
        performSearch(data, '');
      }
    },
    [changeLogRoles, fromUserPage, performSearch]
  );

  const {
    driveLabelsFeatureEnabled,
    canViewDriveLabels,
    driveLabelsAccessGranted,
    getLabelBadgeInfo,
  } = useDriveLabelBadges(driveRoles);

  const columns: GridColDef<Doc>[] = [
    {
      display: 'flex',
      field: 'title',
      headerName: 'Names',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 1,
      renderCell: (params) => {
        const avatar = (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <img
              src={params.row.iconUrl}
              style={{ width: 15, height: 15, borderRadius: 2 }}
            />
            <Tooltip title={params.row.title}>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 240,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {params.row.title}
              </Typography>
            </Tooltip>
            {params.row.driveId && (
              <Tooltip arrow title="Belongs to a Shared Drive">
                <AddToDrive fontSize="small" />
              </Tooltip>
            )}
          </Stack>
        );
        return avatar;
      },
    },
    {
      display: 'flex',
      field: 'owner',
      headerName: 'Owner',
      editable: false,
      sortable: true,
      filterable: true,
      minWidth: 150,
      renderCell: (params) => {
        const ownerEmail = params.row.ownerEmail
          ? params.row.ownerEmail
          : params.row.owners.length > 0
          ? params.row.owners[0].emailAddress
          : '';

        const avatar = (
          <Tooltip title={ownerEmail}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              {ownerEmail !== '' ? (
                <UserAvatar
                  userEmail={ownerEmail}
                  sx={{ width: 24, height: 24 }}
                />
              ) : null}

              <Typography
                variant="body2"
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {ownerEmail !== '' ? ownerEmail : '-'}
              </Typography>
            </Stack>
          </Tooltip>
        );
        return avatar;
      },
    },
    {
      display: 'flex',
      field: 'modifiedDate',
      headerName: 'Last Modified',
      editable: false,
      sortable: true,
      filterable: true,
      minWidth: 115,
      type: 'dateTime',
      valueGetter: (_value, row) => {
        if (row.modifiedDate === 0) return '';
        return new Date(row.modifiedDate);
      },
      valueFormatter: (value) => {
        if (value === '') return '';
        return new Date(value as string).toLocaleDateString();
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      display: 'flex',
      flex: 0.4,
      renderCell: (params) => {
        const avatar = (
          <Stack flexDirection="row" alignItems="center">
            <Tooltip arrow title="Copy">
              <span>
                <IconButton
                  size="small"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await navigator.clipboard.writeText(params.row.id);
                    setSnackbar({
                      open: true,
                      severity: 'info',
                      message: 'ID has been copied to clipboard.',
                    });
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={params.row.id}>
              <Typography variant="body2">{params.row.id}</Typography>
            </Tooltip>
          </Stack>
        );
        return avatar;
      },
    },
    {
      display: 'flex',
      field: 'fileSize',
      headerName: 'File Size',
      editable: false,
      sortable: true,
      filterable: true,
      type: 'number',
      valueGetter: (_value, row) => {
        if (row.mimeType.includes('folder')) {
          return -1;
        } else {
          return row.fileSize;
        }
      },
      valueFormatter: (value) => {
        if (value == null || value === -1) return '-';
        return getFileSize(value);
      },
    },
    {
      display: 'flex',
      field: 'location',
      headerName: 'Location',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 0.5,
      pinnable: false,
      valueGetter: (_value, row) => {
        const name = row.location?.name;
        return name && name.trim() !== '' ? name : '-';
      },
      cellClassName: 'column-cell',
      renderCell: (params) => {
        const locationName = params.row.location?.name;
        if (!locationName || locationName.trim() === '') {
          return <Typography>-</Typography>;
        }
        return (
          <DriveSearchLocation
            params={params}
            loggedInUser={loggedInUser}
            primaryDomain={primaryDomain}
            hierarchy={hierarchy}
            setHierarchy={setHierarchy}
            hierarchyCache={hierarchyCache}
            setHierarchyCache={setHierarchyCache}
            hoveredRowId={hoveredRowId}
            setHoveredRowId={setHoveredRowId}
            isHierarchyLoading={isHierarchyLoading}
            setIsHierarchyLoading={setIsHierarchyLoading}
          />
        );
      },
    },
    {
      display: 'flex',
      field: 'foundUnder',
      headerName: 'Found Under',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 1,
      pinnable: false,
      renderCell: (params) => {
        const avatar = (
          <Tooltip title={params.row.foundUnder}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <UserAvatar
                userEmail={params.row.foundUnder}
                sx={{ width: 24, height: 24 }}
              />
              <Typography
                variant="body2"
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {params.row.foundUnder}
              </Typography>
            </Stack>
          </Tooltip>
        );
        return avatar;
      },
    },
    {
      field: 'trashed',
      headerName: 'Trashed',
      display: 'flex',
      flex: 0.4,
      pinnable: false,
      type: 'boolean',
      valueGetter: (value: boolean) => {
        return value;
      },
    },
    {
      field: 'isShared',
      headerName: 'Shared',
      display: 'flex',
      flex: 0.4,
      pinnable: false,
      type: 'boolean',
      valueGetter: (value: boolean) => {
        return value;
      },
    },
    {
      field: 'docLink',
      headerName: 'Document Link',
      display: 'flex',
      flex: 0.6,
      pinnable: false,
      type: 'boolean',
      renderCell: (params) => {
        const url = generateDriveUrl(
          params.row.mimeType,
          params.row.id,
          loggedInUser,
          primaryDomain
        );
        const avatar = (
          <Tooltip title={url}>
            <Stack direction="row" gap={1} alignItems="center">
              <Tooltip arrow title="Copy">
                <span>
                  <IconButton
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await navigator.clipboard.writeText(url);
                      setSnackbar({
                        open: true,
                        severity: 'info',
                        message: 'Document link has been copied to clipboard.',
                      });
                    }}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              {params.id && (
                <>
                  <Link
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                  >
                    View Document
                  </Link>
                </>
              )}
            </Stack>
          </Tooltip>
        );
        return avatar;
      },
    },
    {
      field: 'lastModifiedBy',
      headerName: 'Last Modified By',
      display: 'flex',
      flex: 0.4,
      pinnable: false,
      type: 'boolean',
      renderCell: (params) => {
        const avatar = (
          <Tooltip title={params.row.foundUnder}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <UserAvatar
                userEmail={params.row.foundUnder}
                sx={{ width: 24, height: 24 }}
              />
              <Typography
                variant="body2"
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {params.row.foundUnder}
              </Typography>
            </Stack>
          </Tooltip>
        );
        return avatar;
      },
    },
    {
      field: 'mimeType',
      headerName: 'Mime Type',
      display: 'flex',
      flex: 0.4,
      pinnable: false,
      type: 'boolean',
      renderCell: (params) => {
        const avatar = (
          <Tooltip title={params.row.mimeType}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {params.row.mimeType}
              </Typography>
            </Stack>
          </Tooltip>
        );
        return avatar;
      },
    },
    ...(driveLabelsFeatureEnabled &&
    canViewDriveLabels &&
    driveLabelsAccessGranted
      ? [
          {
            field: 'labelTitles',
            headerName: 'Labels',
            display: 'flex',
            minWidth: 150,
            flex: 0.5,
            sortable: false,
            filterable: false,
            valueGetter: (_value: any, row: Doc) =>
              row.labelTitles?.join(', ') ?? '',
            renderCell: (params: { row: Doc }) => (
              <DriveDocLabelsCell
                doc={params.row}
                getLabelBadgeInfo={getLabelBadgeInfo}
              />
            ),
          } as GridColDef<Doc>,
        ]
      : []),
    ...((
      searchedDocs.length === 0 ? isOnlyOwnedFilterActive : appliedOnlyOwnedByMe
    )
      ? [
          {
            field: 'visibility',
            headerName: 'Visibility',
            display: 'flex' as const,
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            filterable: false,
            renderCell: (params: { row: Doc }) => {
              const visibilityInfo = getVisibilityInfo(
                params.row,
                primaryDomain
              );
              return (
                <Tooltip title={visibilityInfo.tooltip} arrow>
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                  >
                    {visibilityInfo.icon}
                  </Stack>
                </Tooltip>
              );
            },
          },
        ]
      : []),
    {
      display: 'flex',
      field: 'actions',
      type: 'actions',
      minWidth: 70,
      headerName: 'Actions',
      getActions: (params: GridRowParams<Doc>) => {
        const buttonArray = [];

        const ownerEmail = params.row.ownerEmail
          ? params.row.ownerEmail
          : params.row.owners.length > 0
          ? params.row.owners[0].emailAddress
          : '';

        const isSharedDrive = Boolean(
          params.row.driveId && params.row.driveId !== ''
        );

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanStarDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={
                params.row.starred ? 'Remove from Starred' : 'Add to Starred'
              }
              icon={
                isUpdatingStarredStatus &&
                clickedDoc?.id === params.row.id &&
                clickedDoc?.foundUnder === params.row.foundUnder ? (
                  <CircularProgress size={20} />
                ) : (
                  <Tooltip
                    arrow
                    title={
                      params.row.starred
                        ? 'Remove from Starred'
                        : 'Add to Starred'
                    }
                  >
                    {params.row.starred ? <Star /> : <StarOutline />}
                  </Tooltip>
                )
              }
              onClick={() => {
                if (
                  isAbleToSeeUIElement(
                    changeLogRoles,
                    DriveRoles.CanStarDocs.value
                  )
                ) {
                  setStarDocSelection(params.row);
                  setOpenStarChangeLogDialog(true);
                } else {
                  mutateStarredStatus(params.row);
                  setClickedDoc(params.row);
                }
              }}
            />
          );
        }

        if (
          isAbleToSeeUIElement(driveRoles, DriveRoles.CanViewDocuments.value)
        ) {
          buttonArray.push(
            <GridActionsCellItem
              label={'View'}
              icon={<Pageview />}
              onClick={() => {
                const url = generateDriveUrl(
                  params.row.mimeType,
                  params.row.id,
                  loggedInUser,
                  primaryDomain
                );
                window.open(url, '_blank', 'noopener, noreferrer');
              }}
              showInMenu
            />
          );
        }

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanRenameDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Rename'}
              icon={<DriveFileRenameOutline />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenRenameDialog(true);
              }}
              showInMenu
            />
          );
        }

        if (
          isAbleToSeeUIElement(driveRoles, DriveRoles.CanCopyDocs.value) &&
          !params.row.mimeType.includes('folder') &&
          !params.row.driveId
        ) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Make A Copy'}
              icon={<FileCopy />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenCopyDialog(true);
              }}
              showInMenu
            />
          );
        }

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanShareDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Share'}
              icon={<Share />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenEditShareDialog(true);
              }}
              showInMenu
              disabled={ownerEmail === '' && !isSharedDrive}
            />
          );
        }

        if (params.row.trashed === true) {
          if (
            isAbleToSeeUIElement(driveRoles, DriveRoles.CanRestoreDocs.value)
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label={'Restore'}
                icon={<RestoreFromTrash />}
                onClick={() => {
                  setClickedDoc(params.row);
                  setOpenRestoreDialog(true);
                }}
                showInMenu
              />
            );
          }

          if (
            isAbleToSeeUIElement(driveRoles, DriveRoles.CanDeleteDocs.value)
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label={'Delete Forever'}
                icon={<DeleteForever />}
                onClick={() => {
                  setClickedDoc(params.row);
                  setOpenDeleteDialog(true);
                }}
                showInMenu
              />
            );
          }
        }

        if (
          isAbleToSeeUIElement(driveRoles, DriveRoles.CanTrashDocs.value) &&
          params.row.trashed === false
        ) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Trash'}
              icon={<Delete />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenTrashDialog(true);
              }}
              showInMenu
            />
          );
        }

        return buttonArray;
      },
    },
  ];

  const defaultGridState = {
    columns: {
      columnVisibilityModel: {
        isShared: false,
        trashed: false,
        lastModifiedBy: false,
        mimeType: false,
        docLink: false,
      },
    },
  };

  const isSearchedDocsEmpty = searchedDocs.length === 0;
  const toolbarProps = useMemo(
    () => ({
      driveRoles: driveRoles,
      handleExportMutate: handleExportMutate,
      handleSearch: handleSearch,
      isSearchedDocsEmpty: isSearchedDocsEmpty,
      isExporting: isExporting,
      isLoading: isLoading,
      setIsLoading: setIsLoading,
      handleSubmit: handleSubmit,
      numberOfSearchedUsers: numberOfSearchedUsers,
      erroredUsers: erroredUsers,
    }),
    [
      driveRoles,
      erroredUsers,
      handleExportMutate,
      handleSearch,
      handleSubmit,
      isSearchedDocsEmpty,
      isExporting,
      isLoading,
      numberOfSearchedUsers,
      setIsLoading,
    ]
  );

  return (
    <>
      <SharedDataGridClient
        gridKey={viewType.driveSearchesGridStates?.activeGridState || 'default'}
        getRowId={(row) => `${row.id}-${row.foundUnder}`}
        slots={{
          toolbar: useToolbar(DriveSearchGridToolbar, toolbarProps),
          ...createGridOverlays(
            !searchedDocs ? 'Search Above' : 'Documents/Folders',
            <FindInPage />,
            'Select a source and any optional filters you would like to search by.',
            'Try changing your search term.'
          ),
        }}
        keepNonExistentRowsSelected={false}
        defaultGridState={defaultGridState}
        rows={searchedDocs}
        columns={columns}
        sx={{
          '& .MuiDataGrid-cell.column-cell:hover': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
        }}
        disableRowSelectionOnClick
        gridStateKey="driveSearchesGridStates"
      />
      {clickedDoc && openRenameDialog ? (
        <SharedErrorBoundary section="Drive Search Rename Dialog">
          <DriveSearchRenameDialog
            open={openRenameDialog}
            handleClose={() => {
              setOpenRenameDialog(false);
              setClickedDoc(null);
            }}
            userEmail={loggedInUser}
            selectedDoc={clickedDoc}
            changeLogRoles={changeLogRoles}
            searchedDocs={searchedDocs}
            setSearchedDocs={setSearchedDocs}
          />
        </SharedErrorBoundary>
      ) : null}
      {clickedDoc && openCopyDialog ? (
        <SharedErrorBoundary section="Drive Search Copy Dialog">
          <DriveSearchCopyDialog
            open={openCopyDialog}
            handleClose={() => {
              setOpenCopyDialog(false);
              setClickedDoc(null);
            }}
            selectedDoc={clickedDoc}
            changeLogRoles={changeLogRoles}
            searchedDocs={searchedDocs}
            setSearchedDocs={setSearchedDocs}
          />
        </SharedErrorBoundary>
      ) : null}
      {clickedDoc && openEditShareDialog ? (
        <SharedErrorBoundary section="Drive Search Edit Share Dialog">
          <DriveFileEditPermissionsDialog
            open={openEditShareDialog}
            handleClose={() => setOpenEditShareDialog(false)}
            primaryDomain={primaryDomain}
            selectedDoc={clickedDoc}
            setSelectedDoc={setClickedDoc}
            driveRoles={driveRoles}
            changeLogRoles={changeLogRoles}
            loggedInUser={loggedInUser}
            onVisibilityUpdated={handleVisibilityUpdated}
          />
        </SharedErrorBoundary>
      ) : null}
      {clickedDoc && openRestoreDialog ? (
        <SharedErrorBoundary section="Drive Search Restore Dialog">
          <DriveSearchRestoreDialog
            open={openRestoreDialog}
            handleClose={() => setOpenRestoreDialog(false)}
            selectedDoc={clickedDoc}
            changeLogRoles={changeLogRoles}
            searchedDocs={searchedDocs}
            setSearchedDocs={setSearchedDocs}
          />
        </SharedErrorBoundary>
      ) : null}
      {clickedDoc && openTrashDialog ? (
        <SharedErrorBoundary section="Drive Search Trash Dialog">
          <DriveSearchTrashDialog
            open={openTrashDialog}
            handleClose={() => setOpenTrashDialog(false)}
            selectedDoc={clickedDoc}
            changeLogRoles={changeLogRoles}
            searchedDocs={searchedDocs}
            setSearchedDocs={setSearchedDocs}
          />
        </SharedErrorBoundary>
      ) : null}
      {clickedDoc && openDeleteDialog ? (
        <SharedErrorBoundary section="Drive Search Delete Dialog">
          <DriveSearchDeleteDialog
            open={openDeleteDialog}
            handleClose={() => setOpenDeleteDialog(false)}
            selectedDoc={clickedDoc}
            changeLogRoles={changeLogRoles}
            searchedDocs={searchedDocs}
            setSearchedDocs={setSearchedDocs}
          />
        </SharedErrorBoundary>
      ) : null}
      {openExportChangeLogDialog ? (
        <SharedErrorBoundary section="Drive Search Export Change Log Dialog">
          <ChangeLogDialog
            open={openExportChangeLogDialog}
            handleClose={() => setOpenExportChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={handleExportMutate}
            mutateLoading={isExporting}
          />
        </SharedErrorBoundary>
      ) : null}
      {openStarChangeLogDialog ? (
        <SharedErrorBoundary section="Drive Search Export Star Change Log Dialog">
          <ChangeLogDialog
            open={openStarChangeLogDialog}
            handleClose={() => setOpenStarChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutateStarredStatus(starDocSelection!);
            }}
            mutateLoading={isUpdatingStarredStatus}
          />
        </SharedErrorBoundary>
      ) : null}
      {openSearchChangeLogDialog ? (
        <ChangeLogDialog
          open={openSearchChangeLogDialog}
          handleClose={() => setOpenSearchChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            if (storedSearchData) {
              performSearch(storedSearchData, changeLogMessage);
            }
          }}
          mutateLoading={isLoading}
        />
      ) : null}
    </>
  );
}
function getFileSize(value: number) {
  if (value === -1) return '-';
  if (value === 0) return '0 B';
  if (value < 1000) return value + ' B';
  if (value < 1000000) return Math.round(value / 1000) + ' KB';
  if (value < 1000000000) return Math.round(value / 1000000) + ' MB';
}
